"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import {
  captureJobAlert,
  type CaptureActionResult,
} from "@/app/jobs/alert-actions";
import { capture } from "@/lib/analytics/events";
import { createClient } from "@/lib/supabase/client";

/**
 * The board's alert nudge — the account-free re-engagement capture.
 *
 * Asking a first-time visitor to create an account before we have delivered
 * anything is where the funnel used to end ("Sign in to save"). This asks for
 * one email and nothing else, at the one moment intent is provable: they have
 * just narrowed the board to a specific search and stayed on the results.
 *
 * Triggering rules, in order:
 *  - only on a REFINED search (a keyword or at least one filter) — a nudge on
 *    the unfiltered board has nothing to offer and reads as a popup ad;
 *  - only after DWELL_MS on those results, so it never interrupts someone
 *    mid-typing or bouncing straight back out;
 *  - never for signed-in users (they have the Create alert button) — checked
 *    client-side via Supabase's session (same pattern as HeaderCta) so the
 *    page that mounts this stays static/ISR instead of forcing SSR just to
 *    read auth state for one boolean;
 *  - once per browser after a capture; after a dismissal, not again for
 *    SNOOZE_DAYS. The flag is a first-party UI preference, not analytics
 *    storage, so it needs no consent (see lib/consent.ts).
 *
 * It is a native <dialog> opened with showModal() deliberately: that puts it in
 * the browser's top layer, above the fixed cookie banner. A z-indexed card
 * loses that fight — the micro-survey shipped as one and every click landed on
 * the banner instead (#9).
 */

const SEEN_KEY = "cm.alertnudge.v1";
const DWELL_MS = 5000;
const SNOOZE_DAYS = 14;

/** "done" = captured, never ask again. An ISO date = dismissed, snoozed from then. */
function isSuppressed(): boolean {
  try {
    const v = localStorage.getItem(SEEN_KEY);
    if (!v) return false;
    if (v === "done") return true;
    const at = Date.parse(v);
    if (Number.isNaN(at)) return true;
    return Date.now() - at < SNOOZE_DAYS * 86_400_000;
  } catch {
    // Private mode / storage disabled — never nag rather than nag every visit.
    return true;
  }
}

function suppress(value: string): void {
  try {
    localStorage.setItem(SEEN_KEY, value);
  } catch {
    // Nothing to do — the in-memory state already closed the dialog.
  }
}

export function AlertNudge({
  filters,
  scopeLabel,
}: {
  /** Alert-scope querystring for the current search — "" means unrefined. */
  filters: string;
  /** Human summary of the search, shown so the offer is concrete. */
  scopeLabel: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [result, formAction, pending] = useActionState<
    CaptureActionResult | null,
    FormData
  >(captureJobAlert, null);

  // Signed-in visitors already have the Create alert button — check once on
  // mount, client-side, so the host page can stay static/ISR-cached.
  useEffect(() => {
    let alive = true;
    createClient()
      .auth.getSession()
      .then(({ data }) => {
        if (alive) setAuthed(!!data.session);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Arm the timer for this search. Re-runs on every filter change, so the five
  // seconds are counted against the search the visitor actually settled on.
  useEffect(() => {
    if (authed || !filters || isSuppressed()) return;
    const t = window.setTimeout(() => setOpen(true), DWELL_MS);
    return () => window.clearTimeout(t);
  }, [authed, filters]);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.showModal();
    capture("alert_nudge_shown", { filters });
  }, [open, filters]);

  const close = (reason: "dismissed" | "captured") => {
    if (reason === "dismissed") {
      suppress(new Date().toISOString());
      capture("alert_nudge_dismissed", { filters });
    } else {
      suppress("done");
    }
    dialogRef.current?.close();
    setOpen(false);
  };

  // A stored capture is the conversion — flag it before the dialog is closed so
  // a refresh mid-celebration cannot resurrect the nudge.
  const done = result?.ok === true;
  useEffect(() => {
    if (!done) return;
    suppress("done");
    capture("alert_nudge_captured", {
      state: result?.state ?? "check_inbox",
      filters,
    });
  }, [done, result?.state, filters]);

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className="pitch-dialog"
      aria-labelledby="alert-nudge-title"
      onCancel={(e) => {
        e.preventDefault();
        close("dismissed");
      }}
      onClick={(e) => {
        if (e.target === dialogRef.current) close("dismissed");
      }}
    >
      <div className="overflow-hidden rounded-xl border-2 border-ink bg-white">
        <div className="p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <p className="telex flex items-center gap-2 text-[9.5px] text-brand-700">
              <span aria-hidden className="inline-block h-[6px] w-[6px] bg-current" />
              Job alert
            </p>
            <button
              type="button"
              onClick={() => close(done ? "captured" : "dismissed")}
              aria-label="Close"
              className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-full text-lg leading-none text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              ×
            </button>
          </div>

          {done ? (
            <>
              <h2
                id="alert-nudge-title"
                className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink"
              >
                {result?.state === "already_on"
                  ? "That alert is already on."
                  : "Check your inbox."}
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
                {result?.state === "already_on"
                  ? "We're already watching this search for you — nothing else to do."
                  : "We sent you a one-click link to switch this alert on. No password, no account."}
              </p>
              <button
                type="button"
                onClick={() => close("captured")}
                className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-md border border-neutral-300 px-5 text-sm font-semibold text-ink transition-colors hover:bg-neutral-50"
              >
                Keep browsing
              </button>
            </>
          ) : (
            <>
              <h2
                id="alert-nudge-title"
                className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink"
              >
                Get this search by email.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
                New jobs matching what you&rsquo;re looking at, as they go live.
                Only new listings, never repeats.
              </p>

              <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                <p className="telex text-[8.5px] text-neutral-400">
                  This alert covers
                </p>
                <p className="mt-1 text-[13.5px] font-medium text-ink">
                  {scopeLabel}
                </p>
              </div>

              <form
                action={formAction}
                onSubmit={() => capture("alert_nudge_submit", { filters })}
                className="mt-4"
              >
                <input type="hidden" name="filters" value={filters} />
                <label className="block">
                  <span className="sr-only">Your email</span>
                  <input
                    type="email"
                    name="email"
                    required
                    autoFocus
                    autoComplete="email"
                    placeholder="you@example.org"
                    className="w-full rounded-md border border-neutral-300 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                  />
                </label>

                <label className="mt-3 flex items-start gap-2.5 text-[12.5px] leading-relaxed text-neutral-600">
                  <input
                    type="checkbox"
                    name="terms"
                    value="1"
                    required
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-600 focus:ring-brand-600"
                  />
                  <span>
                    I accept the{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-700 underline underline-offset-2 hover:text-brand-800"
                    >
                      terms
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-700 underline underline-offset-2 hover:text-brand-800"
                    >
                      privacy policy
                    </a>
                    .
                  </span>
                </label>

                {result && !result.ok ? (
                  <p
                    role="alert"
                    className="mt-3 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-sm text-stamp-700"
                  >
                    {result.message}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={pending}
                  className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {pending ? "Setting up…" : "Email me these jobs"}
                </button>
                <button
                  type="button"
                  onClick={() => close("dismissed")}
                  className="mt-2 w-full text-center text-[13px] text-neutral-500 hover:text-ink"
                >
                  No thanks
                </button>
                <p className="telex mt-3 text-center text-[8.5px] text-neutral-400">
                  No account needed · unsubscribe anytime
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </dialog>
  );
}
